import {
  MEDIA_UPLOAD_FILE_TYPE_LABELS,
  MEDIA_UPLOAD_MAX_BYTES_BY_FILE_TYPE,
} from '@/constants/media-upload-size-limits.ts';
import { inferMediaFileTypeFromFileName } from '@/helpers/media-file-type.helper.ts';
import { formatFileSize } from '@/helpers/file.helper.ts';
import { MediaUploadError } from '@/hooks/use-media-upload.ts';
import type { FileType } from '@/types/dtos/media/media.asset.dto.ts';
import type { TranslationKey } from '@/i18n/translation-key';

export type MediaUploadSizeCheck =
  | { ok: true; fileType: FileType; maxBytes: number }
  | { ok: false; error: MediaUploadError };

function fileTooLargeError(params: {
  file: File;
  fileType?: FileType;
  maxBytes: number;
  imageSequenceTotal?: number;
}): MediaUploadError {
  return new MediaUploadError('File exceeds the maximum upload size for this media type', 'FILE_TOO_LARGE', {
    kind: 'quota_blocked',
    preflight: {
      allowed: false,
      availableBytes: params.maxBytes,
      requiredBytes: params.file.size,
      remainingAfterBytes: 0,
      maxFileSizeBytes: params.maxBytes,
      errorCode: 'FILE_TOO_LARGE',
      canPurchaseAdditionalStorage: false,
      details: {
        availableBytes: params.maxBytes,
        requiredBytes: params.file.size,
        fileType: params.fileType,
        fileName: params.file.name,
        imageSequenceTotal: params.imageSequenceTotal,
      },
    },
  });
}

export function validateMediaUploadFileSize(
  file: File,
  options?: { imageSequenceTotal?: number },
): MediaUploadSizeCheck {
  const fileType = inferMediaFileTypeFromFileName(file.name);
  if (!fileType) {
    return {
      ok: false,
      error: new MediaUploadError('This file type is not supported for upload', 'FILE_TOO_LARGE'),
    };
  }

  const maxBytes = MEDIA_UPLOAD_MAX_BYTES_BY_FILE_TYPE[fileType];
  if (file.size > maxBytes) {
    return {
      ok: false,
      error: fileTooLargeError({
        file,
        fileType,
        maxBytes,
        imageSequenceTotal: options?.imageSequenceTotal,
      }),
    };
  }

  return { ok: true, fileType, maxBytes };
}

export function validateMediaUploadFilesSize(files: File[]): MediaUploadSizeCheck {
  if (files.length === 0) {
    return { ok: false, error: new MediaUploadError('No files selected') };
  }

  const isSequence = files.length > 1;

  for (const file of files) {
    const fileType = inferMediaFileTypeFromFileName(file.name);
    if (isSequence && fileType !== 'image') {
      return {
        ok: false,
        error: new MediaUploadError('When selecting multiple files, all must be images (JPEG, PNG, or WebP)'),
      };
    }

    const result = validateMediaUploadFileSize(file, isSequence ? { imageSequenceTotal: files.length } : undefined);
    if (!result.ok) {
      return result;
    }
  }

  if (files.length === 1) {
    return validateMediaUploadFileSize(files[0]);
  }

  return {
    ok: true,
    fileType: 'imageSequence',
    maxBytes: MEDIA_UPLOAD_MAX_BYTES_BY_FILE_TYPE.image,
  };
}

export function mediaUploadFileTypeLabel(fileType?: FileType): string | undefined {
  if (!fileType) {
    return undefined;
  }
  return MEDIA_UPLOAD_FILE_TYPE_LABELS[fileType];
}

type Translate = (key: TranslationKey, params?: Record<string, string | number>) => string;

export function resolveMediaUploadSizeLimitViolationMessage(
  params: {
    fileSizeBytes: number;
    maxFileSizeBytes: number;
    fileType?: FileType;
    fileName?: string;
    imageSequenceTotal?: number;
  },
  t: Translate,
): string {
  const fileSize = formatFileSize(params.fileSizeBytes);
  const limit = formatFileSize(params.maxFileSizeBytes);

  if (params.imageSequenceTotal != null && params.imageSequenceTotal > 1 && params.fileName) {
    return t('media.uploadPreflight.maxFileSizeExceededSequenceImage', {
      fileName: params.fileName,
      fileSize,
      limit,
    });
  }

  if (params.fileName) {
    return t('media.uploadPreflight.maxFileSizeExceededNamed', {
      fileName: params.fileName,
      fileSize,
      limit,
    });
  }

  const typeLabel = mediaUploadFileTypeLabel(params.fileType);
  if (typeLabel) {
    return t('media.uploadPreflight.maxFileSizeExceededForType', {
      typeLabel,
      fileSize,
      limit,
    });
  }

  return t('media.uploadPreflight.maxFileSizeExceeded', { fileSize, limit });
}
